/**
 * @fileoverview Validation-error bubble rendered below a form control. Sole
 * consumer is `FieldUI`, which owns the `open` state and the dismiss handler.
 *
 * The `Tooltip` is deliberately childless: antd synthesizes an empty anchor
 * `<span>` for it, which is how the bubble lands under the control rather than
 * wrapping it. Do not give it children.
 *
 * Memoized because it sits on the keystroke path. The `title` element is built
 * inside the component on purpose — passing it in as a prop would allocate a new
 * element on every parent render and defeat the bail-out entirely.
 */

import { FC, memo } from 'react';
import { Tooltip } from 'antd';

interface FieldErrorTooltipProps {
  /** Message shown inside the bubble, from the field type's `errorMessage` */
  errorMessage: string;
  /** Whether the bubble is visible; owned by the parent */
  open: boolean;
  /** Invoked when the user clicks the bubble to dismiss it */
  onDismiss: () => void;
}

const FieldErrorTooltip: FC<FieldErrorTooltipProps> = memo(({
  errorMessage,
  open,
  onDismiss,
}) => (
  <Tooltip
    color='red'
    title={
      <div onClick={onDismiss}>
        {errorMessage}
      </div>
    }
    open={open}
    placement='bottomLeft'
  />
));

FieldErrorTooltip.displayName = 'FieldErrorTooltip';

export default FieldErrorTooltip;
